import React, { FC, useState, useMemo } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  getColumnsForDocumentGrid,
  getDocumentGridRowsToRender,
} from './profileDocuments.helpers';
import { AddButton } from '../../../../components/simple/AllButtons';
import ProfileAddEditDocument from './profileAddEditDocument';
import {
  useProfileDocumentsState,
  useProfileDocumentsActions,
} from '../../contexts/profileDocuments.context';
import { TDocument } from '../../../../../apis/documents/documents.interfaces';
import {
  TDocumentStatus,
  TDocumentType,
} from '../../../../../apis/documents/documents.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

const ProfileDocuments: FC = () => {
  const { translateText } = useTranslation();
  const { documents, isLoading } = useProfileDocumentsState();
  const {
    setSelectedDocument,
    setIsCreateMode,
    setIsEditMode,
    setIsOpenAddDocumentDialog,
  } = useProfileDocumentsActions();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TDocumentStatus | 'all'>(
    'all'
  );
  const [typeFilter, setTypeFilter] = useState<TDocumentType | 'all'>('all');

  const statusLabel = translateText('documents.status');
  const typeLabel = translateText('documents.documentType');

  const filteredDocuments = useMemo(() => {
    if (!Array.isArray(documents)) {
      return [];
    }
    return (
      documents.filter((doc: TDocument) => {
        // Search filter
        if (searchQuery) {
          const searchLower = searchQuery.toLowerCase();
          if (
            !doc.documentName?.toLowerCase().includes(searchLower) &&
            !doc.documentType?.toLowerCase().includes(searchLower) &&
            !doc.remarks?.toLowerCase().includes(searchLower)
          ) {
            return false;
          }
        }

        // Status filter
        if (statusFilter !== 'all' && doc.status !== statusFilter) {
          return false;
        }

        // Type filter
        if (typeFilter !== 'all' && doc.documentType !== typeFilter) {
          return false;
        }

        return true;
      }) ?? []
    );
  }, [documents, searchQuery, statusFilter, typeFilter]);

  const onRowClickHandler = (data?: TDocument | null) => {
    setSelectedDocument(data ?? null);
    setIsCreateMode(false);
    setIsEditMode(true);
    setIsOpenAddDocumentDialog(true);
  };

  const onAddDocumentClickHandler = () => {
    setSelectedDocument(null);
    setIsCreateMode(true);
    setIsEditMode(false);
    setIsOpenAddDocumentDialog(true);
  };

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }} data-testid="profile-documents-container">
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 2 },
        }}
      >
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ flex: 1 }}>
          {/* Search */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={translateText('profile.documents.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: 44, sm: 40 },
                  fontSize: { xs: '16px', sm: '14px' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
              data-testid="profile-documents-search"
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{statusLabel}</InputLabel>
              <Select
                value={statusFilter}
                label={statusLabel}
                onChange={(e) =>
                  setStatusFilter(e.target.value as TDocumentStatus | 'all')
                }
                sx={{
                  minHeight: { xs: 44, sm: 40 },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="profile-documents-status-filter"
              >
                <MenuItem value="all">{translateText('common.all')}</MenuItem>
                <MenuItem value={TDocumentStatus.SUBMITTED}>
                  {translateText('documents.submitted')}
                </MenuItem>
                <MenuItem value={TDocumentStatus.PENDING}>
                  {translateText('documents.pending')}
                </MenuItem>
                <MenuItem value={TDocumentStatus.VERIFIED}>
                  {translateText('documents.verified')}
                </MenuItem>
                <MenuItem value={TDocumentStatus.REJECTED}>
                  {translateText('documents.rejected')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Type Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{typeLabel}</InputLabel>
              <Select
                value={typeFilter}
                label={typeLabel}
                onChange={(e) =>
                  setTypeFilter(e.target.value as TDocumentType | 'all')
                }
                sx={{
                  minHeight: { xs: 44, sm: 40 },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="profile-documents-type-filter"
              >
                <MenuItem value="all">{translateText('common.all')}</MenuItem>
                <MenuItem value={TDocumentType.BIRTH_CERTIFICATE}>
                  {translateText('documents.birthCertificate')}
                </MenuItem>
                <MenuItem value={TDocumentType.TRANSFER_CERTIFICATE}>
                  {translateText('documents.transferCertificate')}
                </MenuItem>
                <MenuItem value={TDocumentType.PREVIOUS_MARKSHEET}>
                  {translateText('documents.previousMarksheet')}
                </MenuItem>
                <MenuItem value={TDocumentType.MEDICAL_CERTIFICATE}>
                  {translateText('documents.medicalCertificate')}
                </MenuItem>
                <MenuItem value={TDocumentType.AADHAR_CARD}>
                  {translateText('documents.aadharCard')}
                </MenuItem>
                <MenuItem value={TDocumentType.PAN_CARD}>
                  {translateText('documents.panCard')}
                </MenuItem>
                <MenuItem value={TDocumentType.SALARY_SLIP}>
                  {translateText('profile.documents.filters.payslips')}
                </MenuItem>
                <MenuItem value={TDocumentType.HR_DOCUMENT}>
                  {translateText('profile.documents.filters.hr')}
                </MenuItem>
                <MenuItem value={TDocumentType.PHOTO}>
                  {translateText('documents.photo')}
                </MenuItem>
                <MenuItem value={TDocumentType.OTHER}>
                  {translateText('documents.other')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <AddButton
          label={translateText('profile.documents.uploadDocument')}
          onClickHandler={onAddDocumentClickHandler}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minHeight: { xs: 44, sm: 36 },
          }}
          data-testid="profile-documents-upload-button"
        />
      </Grid>

      <Grid item xs={12}>
        <PaginatedGrid
          rows={getDocumentGridRowsToRender(filteredDocuments) ?? []}
          columns={getColumnsForDocumentGrid(onRowClickHandler)}
          loading={isLoading}
          dataTestId="profile-documents-grid"
          serverSide={false}
          totalCount={filteredDocuments.length}
          getRowId={(row) => row.documentId}
          hideColumnsOnMobile={['id']}
        />
      </Grid>

      <ProfileAddEditDocument />
    </Grid>
  );
};

export default ProfileDocuments;
